import { secondsToMilliseconds } from 'date-fns';
import { useSetAtom } from 'jotai';
import { useCallback } from 'react';

import { getAdmissionWaitConfig } from '../../config';
import { logger } from '../../datadog/logger';
import { useAtomCallbackMemoized } from '../../hooks/useAtomCallbackMemoized';
import { toast } from '../../toasts';
import { removeImmutablyFromSet } from '../../utils';
import { participantsByIdAtom, roomAtom } from '../../video-sdk';
import { joiningVisitorIdsAtom, visitorAdmissionTimesAtom, waitingVisitorsAtom } from '../state';

export function useWaitForClientToJoinCallback() {
  const getRoom = useAtomCallbackMemoized((get) => get(roomAtom), []);
  const getParticipantsByVisitorId = useAtomCallbackMemoized((get) => get(participantsByIdAtom), []);
  const setJoiningVisitorIds = useSetAtom(joiningVisitorIdsAtom);
  const hasOtherJoiningVisitors = useAtomCallbackMemoized((get) => get(joiningVisitorIdsAtom)?.size, []);
  const getWaitingVisitors = useAtomCallbackMemoized((get) => get(waitingVisitorsAtom), []);
  const setVisitorAdmissionTimes = useSetAtom(visitorAdmissionTimesAtom);

  const waitForClientToJoin = useCallback(
    ({ visitorId, onTimeout, onFailure }: { visitorId: string; onTimeout?: () => void; onFailure?: () => void }) => {
      const { timeoutSeconds, waitWhileVisitorPresent } = getAdmissionWaitConfig();
      return setTimeout(() => {
        const visitor = getWaitingVisitors()[visitorId];
        const visitorIsStillWaiting = Boolean(visitor);
        const shouldWaitForClientToJoin = waitWhileVisitorPresent && visitorIsStillWaiting;

        const joinedRoomOrRoomEnded = getParticipantsByVisitorId()[visitorId] || !getRoom();

        // recur until the visitor joins, room ends, or the visitor is no longer
        // waiting
        if (!joinedRoomOrRoomEnded && shouldWaitForClientToJoin) {
          waitForClientToJoin({ visitorId, onTimeout, onFailure });
          return;
        }

        onTimeout?.();
        setJoiningVisitorIds((prev) => removeImmutablyFromSet(prev, visitorId));
        setVisitorAdmissionTimes((prev) => {
          const updated = { ...prev };
          delete updated[visitorId];
          return updated;
        });
        if (hasOtherJoiningVisitors() || joinedRoomOrRoomEnded) {
          return;
        }
        toast.error('The patient may have left the waiting room while being admitted', { purpose: 'system' });
        logger.warn('Timed out waiting for visitor to join', { visitorId });
        onFailure?.();
      }, secondsToMilliseconds(timeoutSeconds));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return waitForClientToJoin;
}
